/**
 * API de farmacia: catálogo, stock, kardex, compras, ventas y alertas.
 */
import { apiFetch } from "./client";

export type CategoriaProducto = "MEDICAMENTO" | "INSUMO" | "MATERIAL" | "OTRO";
export type MetodoPago = "EFECTIVO" | "YAPE" | "PLIN" | "TRANSFERENCIA" | "TARJETA";
export type TipoAjuste =
  | "AJUSTE_POSITIVO"
  | "AJUSTE_NEGATIVO"
  | "MERMA"
  | "VENCIMIENTO";

export interface Producto {
  id: number;
  codigo: string;
  nombre: string;
  principio_activo: string | null;
  concentracion: string | null;
  forma_farmaceutica: string | null;
  presentacion: string | null;
  unidad: string;
  categoria: CategoriaProducto;
  laboratorio: string | null;
  registro_sanitario: string | null;
  requiere_receta: boolean;
  es_controlado: boolean;
  afecto_igv: boolean;
  precio_venta: string | number;
  stock_minimo: number;
  activo: boolean;
  /** Suma de sus lotes. Se calcula al leer, no vive en la tabla. */
  stock: number;
  proximo_vencimiento: string | null;
}

export interface ProductoCreate {
  codigo: string;
  nombre: string;
  principio_activo?: string | null;
  concentracion?: string | null;
  forma_farmaceutica?: string | null;
  presentacion?: string | null;
  unidad?: string;
  categoria?: CategoriaProducto;
  laboratorio?: string | null;
  registro_sanitario?: string | null;
  requiere_receta?: boolean;
  es_controlado?: boolean;
  afecto_igv?: boolean;
  precio_venta?: number;
  stock_minimo?: number;
}

export type ProductoUpdate = Partial<ProductoCreate> & { activo?: boolean };

export interface Lote {
  id: number;
  producto_id: number;
  producto_nombre: string;
  sede_id: number;
  sede_nombre: string | null;
  numero: string;
  fecha_vencimiento: string | null;
  costo_unitario: string | number;
  cantidad: number;
  vencido: boolean;
  dias_para_vencer: number | null;
}

export interface Movimiento {
  id: number;
  fecha: string;
  producto_id: number;
  producto_nombre: string;
  lote_numero: string | null;
  lote_vencimiento: string | null;
  sede_id: number;
  tipo: string;
  es_ingreso: boolean;
  cantidad: number;
  costo_unitario: string | number | null;
  precio_unitario: string | number | null;
  saldo: number;
  /** Costo PEPS del movimiento y valor del inventario después de él. */
  costo_total: string | number | null;
  saldo_valorizado: string | number | null;
  motivo: string | null;
  registrado_por_nombre: string | null;
}

export interface Proveedor {
  id: number;
  ruc: string | null;
  razon_social: string;
  contacto: string | null;
  telefono: string | null;
  email: string | null;
  direccion: string | null;
  activo: boolean;
}

export interface CompraItem {
  id: number;
  producto_id: number;
  producto_nombre: string;
  numero_lote: string;
  fecha_vencimiento: string | null;
  cantidad: number;
  costo_unitario: string | number;
  subtotal: string | number;
}

export interface Compra {
  id: number;
  sede_id: number;
  sede_nombre: string | null;
  proveedor_id: number | null;
  proveedor_nombre: string | null;
  tipo_documento: string;
  numero_documento: string | null;
  fecha: string;
  total: string | number;
  estado: "BORRADOR" | "CONFIRMADA" | "ANULADA";
  observacion: string | null;
  registrado_por_nombre: string | null;
  items: CompraItem[];
}

export interface CompraCreate {
  sede_id: number;
  proveedor_id?: number | null;
  tipo_documento?: "FACTURA" | "BOLETA" | "GUIA" | "OTRO";
  numero_documento?: string | null;
  fecha: string;
  observacion?: string | null;
  items: {
    producto_id: number;
    numero_lote: string;
    fecha_vencimiento?: string | null;
    cantidad: number;
    costo_unitario: number;
  }[];
}

export interface VentaItem {
  id: number;
  producto_id: number;
  producto_nombre: string;
  lote_numero: string | null;
  cantidad: number;
  precio_unitario: string | number;
  subtotal: string | number;
  costo_total: string | number | null;
  margen: string | number | null;
}

export interface Venta {
  id: number;
  sede_id: number;
  paciente_id: number | null;
  paciente_nombre: string | null;
  receta_id: number | null;
  fecha: string;
  total: string | number;
  estado: "PENDIENTE" | "PAGADA" | "ANULADA";
  metodo_pago: string | null;
  observacion: string | null;
  despachado_por_nombre: string | null;
  items: VentaItem[];
}

export interface VentaCreate {
  sede_id: number;
  paciente_id?: number | null;
  receta_id?: number | null;
  metodo_pago?: MetodoPago;
  observacion?: string | null;
  items: {
    producto_id: number;
    cantidad: number;
    precio_unitario?: number | null;
    receta_item_id?: number | null;
  }[];
}

export interface ValorizacionItem {
  producto_id: number;
  codigo: string;
  producto_nombre: string;
  cantidad: number;
  valor_costo: string | number;
  costo_promedio: string | number;
  precio_venta: string | number;
  valor_venta: string | number;
}

export interface Valorizacion {
  /** PEPS. No se cambia: cambiarlo haría incomparables dos inventarios. */
  metodo: string;
  total_costo: string | number;
  total_venta: string | number;
  total_unidades: number;
  items: ValorizacionItem[];
}

export interface ResumenVentas {
  desde: string;
  hasta: string;
  ventas: number;
  ingresos: string | number;
  costo: string | number;
  margen: string | number;
}

export interface AlertaStock {
  producto_id: number;
  producto_nombre: string;
  stock: number;
  stock_minimo: number;
}

export interface Alertas {
  vencidos: Lote[];
  por_vencer: Lote[];
  bajo_minimo: AlertaStock[];
}

const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

export function listProductos(params: {
  q?: string;
  sede_id?: number;
  incluir_inactivos?: boolean;
} = {}): Promise<Producto[]> {
  return apiFetch<Producto[]>(`/v1/farmacia/productos${qs(params)}`);
}

export function createProducto(payload: ProductoCreate): Promise<Producto> {
  return apiFetch<Producto>("/v1/farmacia/productos", { method: "POST", body: payload });
}

export function updateProducto(id: number, payload: ProductoUpdate): Promise<Producto> {
  return apiFetch<Producto>(`/v1/farmacia/productos/${id}`, {
    method: "PATCH",
    body: payload,
  });
}

export function listLotesDeProducto(id: number, sede_id?: number): Promise<Lote[]> {
  return apiFetch<Lote[]>(`/v1/farmacia/productos/${id}/lotes${qs({ sede_id })}`);
}

export function listStock(params: { sede_id?: number; solo_con_stock?: boolean } = {}): Promise<Lote[]> {
  return apiFetch<Lote[]>(`/v1/farmacia/stock${qs(params)}`);
}

export function listKardex(params: {
  producto_id?: number;
  sede_id?: number;
  limit?: number;
} = {}): Promise<Movimiento[]> {
  return apiFetch<Movimiento[]>(`/v1/farmacia/kardex${qs(params)}`);
}

export function getAlertas(sede_id?: number): Promise<Alertas> {
  return apiFetch<Alertas>(`/v1/farmacia/alertas${qs({ sede_id })}`);
}

export function getValorizacion(sede_id?: number): Promise<Valorizacion> {
  return apiFetch<Valorizacion>(`/v1/farmacia/valorizacion${qs({ sede_id })}`);
}

export function getResumenVentas(params: { desde?: string; hasta?: string } = {}): Promise<ResumenVentas> {
  return apiFetch<ResumenVentas>(`/v1/farmacia/resumen-ventas${qs(params)}`);
}

export function listProveedores(): Promise<Proveedor[]> {
  return apiFetch<Proveedor[]>("/v1/farmacia/proveedores");
}

export function createProveedor(payload: {
  razon_social: string;
  ruc?: string | null;
  telefono?: string | null;
  contacto?: string | null;
}): Promise<Proveedor> {
  return apiFetch<Proveedor>("/v1/farmacia/proveedores", { method: "POST", body: payload });
}

export function listCompras(estado?: string): Promise<Compra[]> {
  return apiFetch<Compra[]>(`/v1/farmacia/compras${qs({ estado })}`);
}

export function createCompra(payload: CompraCreate, confirmar = true): Promise<Compra> {
  return apiFetch<Compra>(`/v1/farmacia/compras${qs({ confirmar })}`, {
    method: "POST",
    body: payload,
  });
}

export function confirmarCompra(id: number): Promise<Compra> {
  return apiFetch<Compra>(`/v1/farmacia/compras/${id}/confirmar`, { method: "POST" });
}

export function crearAjuste(payload: {
  lote_id: number;
  tipo: TipoAjuste;
  cantidad: number;
  motivo: string;
}): Promise<Movimiento> {
  return apiFetch<Movimiento>("/v1/farmacia/ajustes", { method: "POST", body: payload });
}

export function listVentas(limit = 100): Promise<Venta[]> {
  return apiFetch<Venta[]>(`/v1/farmacia/ventas${qs({ limit })}`);
}

export function crearVenta(payload: VentaCreate): Promise<Venta> {
  return apiFetch<Venta>("/v1/farmacia/ventas", { method: "POST", body: payload });
}

export function anularVenta(id: number, motivo: string): Promise<Venta> {
  return apiFetch<Venta>(`/v1/farmacia/ventas/${id}/anular`, {
    method: "POST",
    body: { motivo },
  });
}
